<script>
  import { format } from 'svelte-i18n';

  import { ActivityEventIcons } from '@client/components/activity-event-icons';
  import { Icon } from '@client/components/icon';

  import { isBizcardMsglog } from '@client/utils/activity';

  import { convertHTMLEntities, highlight } from '@common/utils/strings';

  export let row = {};
  export let props = {};

  const annotateRecipients = (field) => {
    const recipients = convertHTMLEntities(row.searchObject[field] || '');

    return recipients
      .split(', ')
      .map((recipient) => {
        const isLast = recipient === lastRecipient;
        const isDoNotTrack = row.notrack[recipient] || false;

        let decorated = recipient;

        if (recipient.length) {
          if (isLast) {
            decorated = `<u class="last-active">${decorated}</u>`;
          }

          if (isDoNotTrack && recipient.length) {
            decorated = `<span class="do-not-track" data-bs-toggle="tooltip" title="Tracking Disabled"><i class="os-icon-eye-off"></i></span> ${decorated}`;
          }
        }

        return decorated;
      })
      .join(', ');
  };

  // A business card has no recipient — the visitor is anonymous. Label it the same way the
  // map marker popover does rather than rendering an empty "To:".
  $: bizcard = isBizcardMsglog(row);
  $: to = bizcard
    ? $format('label.BIZCARD_ANONYMOUS_VISITOR')
    : highlight(props.searchText, annotateRecipients('to'), row);
  $: cc = highlight(props.searchText, annotateRecipients('cc'), row);
  $: bcc = highlight(props.searchText, annotateRecipients('bcc'), row);
  $: subject = highlight(props.searchText, convertHTMLEntities(row.msgsubject || ''), row);
  $: tags = row.tags?.split(', ') ?? [];
  $: lastRecipient = row?.lastactivity?.recipient;
  $: attachments = Object.values(
    row.activity.reduce((acc, { pitchinfo }) => {
      if (pitchinfo) {
        pitchinfo.forEach((pitch) => {
          if (!acc[pitch.filename]) {
            acc[pitch.filename] = pitch;
          }
        });
      }

      return acc;
    }, {})
  );
</script>

<div class="email-cell">
  <div class="details">
    <div class="subject">{@html subject}</div>
    <div>
      {$format('label.TO')}:
      {#if bizcard}
        {to}
      {:else}
        {@html to.replace(lastRecipient, `<u class="last-active">${lastRecipient}</u>`)}
      {/if}
    </div>

    {#if cc}
      <div>{$format('label.CC')}: {@html cc.replace(lastRecipient, `<u class="last-active">${lastRecipient}</u>`)}</div>
    {/if}

    {#if bcc}
      <div>
        {$format('label.BCC')}:
        {@html bcc.replace(lastRecipient, `<u class="last-active">${lastRecipient}</u>`)}
      </div>
    {/if}

    <div class="attachments">
      {#each attachments as attachment}
        {#if attachment.filename}
          <div class="attachment">
            <Icon name="paperclip" />
            {@html highlight(props.searchText, convertHTMLEntities(attachment.filename))}
          </div>
        {/if}

        {#if attachment.url}
          <div class="attachment">
            <Icon name="video" />
            {@html highlight(props.searchText, convertHTMLEntities(attachment.url))}
          </div>
        {/if}
      {/each}
    </div>

    {#if tags.length}
      <div class="tags">
        {#each tags as tag}
          <div class="tag">
            <Icon name="tag" />
            {tag}
          </div>
        {/each}
      </div>
    {/if}

    {#if !props.hideIcons}
      <div class="events">
        <ActivityEventIcons bind:data={row} showTooltip />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .email-cell {
    align-items: center;
    display: grid;

    .details {
      overflow: hidden;
      width: 100%;
      white-space: pre-line;

      > div {
        word-break: break-word;
        word-wrap: break-word;
      }
    }

    :global(.last-active) {
      background-color: var(--color-yellow-100);
    }

    :global(.do-not-track) {
      align-items: center;
      background-color: var(--color-red-100);
      border-radius: 100%;
      color: var(--color-red-400);
      display: inline-flex;
      font-size: var(--font-size-xsmall);
      height: 16px;
      justify-content: center;
      width: 16px;
    }

    .subject {
      font-weight: var(--font-weight-medium);
    }

    .tags {
      align-items: center;
      display: flex;
      gap: 4px;
      margin: 6px 0 0;

      .tag {
        align-items: center;
        background-color: var(--color-white);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-base);
        display: flex;
        gap: 4px;
        font-size: var(--font-size-medium);
        padding: var(--padding-small) var(--padding-base);

        > :global(i) {
          line-height: 1em;
        }
      }
    }

    .attachments {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin: 6px 0 0 0;

      .attachment {
        align-items: center;
        background-color: var(--color-white);
        border: 1px solid var(--color-gray-300);
        border-radius: 20px;
        color: var(--color-text);
        display: inline-flex;
        font-size: var(--font-size-medium);
        gap: 4px;
        padding: var(--padding-small) var(--padding-base);
        white-space: nowrap;

        > :global(i) {
          line-height: 1em;
        }
      }
    }

    .events {
      margin: 6px 0 0;
    }
  }

  @media only screen and (max-width: 800px) {
    .email-cell {
      .attachments {
        margin-top: var(--margin-base);

        .attachment {
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
</style>
